/**
 * Pure helpers for the Inventory Risk by Product page (no React, no I/O).
 * Calculations live in the backend (control-tower api/inventory-risk); this
 * only shapes requests and formats the response.
 */

// Table views. "Needs attention" = the product has at least one Out of Stock,
// Critical or At Risk SKU (computed by the API, not a product-level bucket).
export const VIEW_TABS = [
  { value: "attention", label: "Needs attention" },
  { value: "all", label: "All products" },
];
export const DEFAULT_VIEW = "attention";

// Order is the order segments are drawn in the risk-profile bar (worst first).
export const RISK_SEGMENTS = [
  { key: "out_of_stock", short: "Out of Stock", label: "Out of Stock — 0 on hand, has sales", color: "#8B1A1A" },
  { key: "critical", short: "Critical", label: "Critical — ≤5 days cover", color: "#EF4444" },
  { key: "at_risk", short: "At Risk", label: "At Risk — 5–15 days", color: "#F97316" },
  { key: "watch", short: "Watch", label: "Watch — 15–30 days", color: "#EAB308" },
  { key: "low", short: "Low", label: "Low — 30–45 days", color: "#22C55E" },
  { key: "healthy", short: "Healthy", label: "Healthy — >45 days", color: "#94A3B8" },
  { key: "no_demand", short: "No demand", label: "No demand — stock, no sales", color: "#CBD5E1" },
];

export const RISK_BY_KEY = Object.fromEntries(RISK_SEGMENTS.map((segment) => [segment.key, segment]));

// "Has SKUs in" filter: a product matches when it has a SKU in ANY selected bucket.
export const HAS_SKU_FILTERS = ["out_of_stock", "critical", "at_risk", "watch", "low"].map(
  (key) => RISK_BY_KEY[key],
);

// Day-of-cover buckets a client can set (Client Config → Dashboard settings).
// The API returns the client's limits as `risk_bands`; these are the defaults
// it uses when the client has none. Limits are "up to N days".
export const RISK_BAND_KEYS = ["critical", "at_risk", "watch", "low"];
export const DEFAULT_RISK_BANDS = { critical: 5, at_risk: 15, watch: 30, low: 45 };

/** Enabled [key, upper days] pairs, worst first. Accepts `risk_bands` or its `bands`. */
export const enabledRiskBands = (riskBands) => {
  const bands = riskBands?.bands || riskBands || DEFAULT_RISK_BANDS;
  return RISK_BAND_KEYS.filter((key) => bands[key] != null).map((key) => [key, bands[key]]);
};

/** Day range per bucket: { critical: "≤5 days", at_risk: "5–15 days", …, healthy: ">45 days" }. */
export const riskDayRanges = (riskBands) => {
  const ranges = {};
  let lower = null;
  enabledRiskBands(riskBands).forEach(([key, upper]) => {
    ranges[key] = lower == null ? `≤${upper} days` : `${lower}–${upper} days`;
    lower = upper;
  });
  ranges.healthy = `>${lower} days`;
  return ranges;
};

/** RISK_SEGMENTS for a client: buckets it switched off removed, labels from its limits. */
export const riskSegmentsFor = (riskBands) => {
  const ranges = riskDayRanges(riskBands);
  return RISK_SEGMENTS.filter(
    (segment) => !RISK_BAND_KEYS.includes(segment.key) || ranges[segment.key],
  ).map((segment) =>
    ranges[segment.key]
      ? {
          ...segment,
          label: `${segment.short} — ${ranges[segment.key]}${segment.key === "critical" ? " cover" : ""}`,
        }
      : segment,
  );
};

/** HAS_SKU_FILTERS without the buckets the client switched off. */
export const hasSkuFiltersFor = (riskBands) => {
  const enabled = new Set(riskSegmentsFor(riskBands).map((segment) => segment.key));
  return HAS_SKU_FILTERS.filter((segment) => enabled.has(segment.key));
};

// What each bucket calls for, shown on its KPI card.
const RISK_ACTIONS = {
  critical: "reorder now",
  at_risk: "reorder soon",
  watch: "monitor weekly",
  low: "plan next order",
  out_of_stock: "0 on hand, still selling",
};

/** KPI cards, in display order: the client's buckets, then Out of Stock. */
export const riskCardsFor = (riskBands) => {
  const ranges = riskDayRanges(riskBands);
  return [...enabledRiskBands(riskBands).map(([key]) => key), "out_of_stock"].map((key) => ({
    key,
    label: ranges[key] ? `${RISK_BY_KEY[key].short} (${ranges[key]})` : RISK_BY_KEY[key].short,
    action: RISK_ACTIONS[key],
  }));
};

export const HISTORY_DAY_TABS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

export const DEFAULT_SORT = { field: "revenue_at_risk", dir: "desc" };

/** Query params for GET /inventory-risk. `page` in the UI is 0-based. */
export const buildInventoryRiskParams = ({
  clientId,
  warehouseId,
  page = 0,
  rowsPerPage = 25,
  sort = DEFAULT_SORT.field,
  dir = DEFAULT_SORT.dir,
  search = "",
  view = "all",
  risks = [],
} = {}) => {
  const params = {
    warehouse_id: warehouseId,
    page: page + 1,
    limit: rowsPerPage,
    sort,
    dir,
  };
  if (clientId) params.client_id = clientId;
  const trimmed = String(search || "").trim();
  if (trimmed) params.search = trimmed;
  if (view === "attention") params.view = "attention";
  const selected = HAS_SKU_FILTERS.map((f) => f.key).filter((key) => (risks || []).includes(key));
  if (selected.length) params.risk = selected.join(",");
  return params;
};

/** Toggle one bucket in the "Has SKUs in" selection. */
export const toggleRisk = (risks, key) =>
  (risks || []).includes(key) ? risks.filter((value) => value !== key) : [...(risks || []), key];

/** Query params for GET /inventory-risk-history (one SKU, lazy). */
export const buildHistoryParams = ({ clientId, warehouseId, productId, sku, days = 30 }) => {
  const params = { warehouse_id: warehouseId, product_id: productId, sku, days };
  if (clientId) params.client_id = clientId;
  return params;
};

/** Plain-language coverage of a history response (never implies missing days are zero). */
export const historyCoverageNote = (history, formatDay = (value) => value) => {
  if (!history) return "";
  if (!history.history_start) return "No inventory snapshots for this SKU at this warehouse yet.";
  const parts = [];
  if (history.history_start > history.window?.start) {
    parts.push(`Inventory history starts ${formatDay(history.history_start)}`);
  }
  if (history.missing_snapshot_days) {
    parts.push(`${history.missing_snapshot_days} day${history.missing_snapshot_days === 1 ? "" : "s"} without a snapshot (shown as gaps)`);
  }
  if (history.sales_through && history.sales_through < history.window?.end) {
    parts.push(`sales data through ${formatDay(history.sales_through)}`);
  }
  return parts.join(" · ");
};

/**
 * "Now: 5 on hand · Last snapshot: 25 (start of Sep 29) · −20 since" — reconciles the
 * live on-hand in the SKU table with the chart, whose line is daily start-of-day
 * snapshots. The latest snapshot is the last non-gap point; the delta is omitted
 * when the two agree.
 */
export const snapshotComparisonParts = (history, liveOnHand, formatDay = (value) => value) => {
  const live = liveOnHand == null || !Number.isFinite(Number(liveOnHand)) ? null : Number(liveOnHand);
  const parts = live == null ? [] : [`Now: ${fmtUnits(live)} on hand`];
  const last = [...(history?.series || [])].reverse().find((point) => point.in_hand != null);
  if (!history?.history_start || !last) return [...parts, "No snapshot history yet"];
  parts.push(`Last snapshot: ${fmtUnits(last.in_hand)} (start of ${formatDay(last.date)})`);
  const delta = live == null ? 0 : Math.round(live) - Math.round(Number(last.in_hand));
  if (delta) parts.push(`${delta > 0 ? "+" : "−"}${fmtUnits(Math.abs(delta))} since`);
  return parts;
};

/** Percent widths of each non-empty risk segment for one product row. */
export const riskProfileSegments = (row) => {
  const total = RISK_SEGMENTS.reduce(
    (sum, segment) => sum + (Number(row?.[segment.key]) || 0),
    0,
  );
  if (!total) return [];
  return RISK_SEGMENTS.filter((segment) => Number(row?.[segment.key]) > 0).map(
    (segment) => ({
      ...segment,
      count: Number(row[segment.key]),
      pct: (Number(row[segment.key]) / total) * 100,
    }),
  );
};

export const fmtUnits = (value) =>
  value == null || !Number.isFinite(Number(value))
    ? "—"
    : Math.round(Number(value)).toLocaleString("en-US");

export const fmtMoney = (value) =>
  value == null || !Number.isFinite(Number(value))
    ? "—"
    : `$${Math.round(Number(value)).toLocaleString("en-US")}`;

// null days of cover means "no demand", never infinite.
export const fmtDays = (value) =>
  value == null || !Number.isFinite(Number(value))
    ? "No demand"
    : Number(value).toFixed(1);

export const inventoryRiskErrorMessage = (err) => {
  const data = err?.response?.data;
  if (data?.data?.code === "PRODUCT_NOT_FOUND" || data?.data?.code === "SKU_NOT_FOUND") {
    return data.message;
  }
  if (data?.data?.code === "WAREHOUSE_REQUIRED") {
    return "Select a warehouse — inventory risk is calculated for one warehouse at a time.";
  }
  return data?.message || err?.message || "Unable to load inventory risk.";
};
